// TQ #5336: Cockpit Downloads-Area Frontend.
// Spec: memory/cockpit-downloads-area.md, Backend TQ #5335 (GET /api/org/:slug/downloads/catalog).
//
// Eine zentrale Page mit Card-Grid pro Produkt + Plattform-Download-Buttons.
// MCP-Server-Sub-Liste expandable inline (kein separater Dialog noetig).
// OS-Detection per navigator.userAgent → "Empfohlen"-Badge auf passendem Button.

import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'
import { DownloadIcon } from '../components/ui/Icons'
import { isPersonalMode } from '../utils/cockpitMode'

interface Platform {
  os: string
  arch: string
  url: string
  size_bytes?: number
  file_name?: string
}

interface Product {
  slug: string
  name: string
  description?: string
  category?: string
  version_latest?: string
  // TQ #6007: backend kann platforms=null liefern wenn Release keine
  // Binaries hat. Null-safe gerendert via (platforms ?? []) im JSX.
  platforms?: Platform[] | null
  sub_products?: Product[] | null
}

interface CatalogResponse {
  products: Product[]
}

interface Props {
  organization: { slug: string; public_token?: string; member_role?: string }
}

const OS_LABEL: Record<string, string> = {
  darwin: 'macOS',
  macos: 'macOS',
  mac: 'macOS',
  windows: 'Windows',
  win: 'Windows',
  linux: 'Linux',
}

const OS_ICON: Record<string, string> = {
  darwin: '🍎',
  macos: '🍎',
  mac: '🍎',
  windows: '🪟',
  win: '🪟',
  linux: '🐧',
}

function detectClientOS(): string {
  const ua = navigator.userAgent.toLowerCase()
  if (ua.includes('mac') || ua.includes('darwin')) return 'darwin'
  if (ua.includes('win')) return 'windows'
  if (ua.includes('linux')) return 'linux'
  return ''
}

function formatBytes(b?: number): string {
  if (!b || b <= 0) return ''
  const mb = b / 1024 / 1024
  if (mb < 1) return `${(b / 1024).toFixed(0)} KB`
  return `${mb.toFixed(1)} MB`
}

function normalizeOS(os: string): string {
  return os.toLowerCase().trim()
}

export default function Downloads({ organization }: Props) {
  const { api } = useAuth()
  const [data, setData] = useState<CatalogResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [mcpExpanded, setMcpExpanded] = useState(false)

  const clientOS = useMemo(detectClientOS, [])
  // Task-2026-00451 Teil 2 (07.10.2026, Marco): Downloads nur noch fuer
  // Owner/Admin. Bisher (Patric 2026-05-20, V1) fuer jedes Mitglied und ohne
  // Subscription-Filter sichtbar — diese Rollen-Pruefung ist neu und kommt
  // zusaetzlich zum Layout.tsx-Nav-Gate (Defense-in-Depth, analog Settings.tsx
  // isAdminOrOwner-Checks): verhindert Zugriff auch per Direkt-URL, nicht nur
  // Ausblenden aus der Navigation. Personal-Mode bleibt unveraendert (Pseudo-
  // Org hat kein "anderes Mitglied", dort gibt es nichts zu beschraenken).
  const personal = isPersonalMode()
  const isAdminOrOwner = organization.member_role === 'owner' || organization.member_role === 'admin'
  const allowed = personal || isAdminOrOwner

  useEffect(() => {
    if (!allowed) return
    let cancelled = false
    setLoading(true)
    setError(null)
    // TQ #6906 Refactor 2026-06-03: im Personal-Mode (with.it-sce.de) gibt
    // es keine Org → wir nutzen den public downloads-service Endpoint
    // (lite Schema), im Org/Staff weiter den vollen Catalog mit
    // sub_products + Lizenz-gates.
    const url = personal
      ? '/api/downloads/products'
      : `/api/org/${organization.public_token || organization.slug}/downloads/catalog`
    api!
      .get<CatalogResponse>(url)
      .then((r) => {
        if (cancelled) return
        // Personal-Endpoint liefert {products: [{slug, name, latest_release: {binaries:[]}}]}.
        // Wir mappen das auf das vom Org-Catalog erwartete Schema (platforms[]).
        if (personal) {
          const rawList: any[] = (r.data as any)?.products || []
          const products = rawList.map((p: any) => {
            const ver = p.latest_version || p.latest_release?.version || p.version_latest
            const bins: any[] = p.latest_release?.binaries || p.platforms || []
            const platforms: Platform[] = bins.map((b: any) => ({
              os: b.os || b.platform || '',
              arch: b.architecture || b.arch || '',
              url: b.download_url || b.url || `https://downloads.it-sce.de/api/download/${p.slug}/${ver}/${b.os || b.platform}`,
              size_bytes: b.file_size || b.size_bytes,
              file_name: b.file_name,
            }))
            return {
              slug: p.slug,
              name: p.name,
              description: p.description,
              category: p.category,
              version_latest: ver,
              platforms,
            } as Product
          })
          setData({ products })
        } else {
          setData(r.data)
        }
      })
      .catch((e) => {
        if (!cancelled) {
          const err = e as { response?: { data?: { error?: string } } }
          setError(err.response?.data?.error ?? 'Catalog konnte nicht geladen werden.')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [api, organization.public_token, organization.slug, allowed, personal])

  if (!allowed) {
    return (
      <div style={{ padding: '2rem' }}>
        <div style={{
          padding: '1rem', background: '#f9fafb', border: '1px solid #e5e7eb',
          borderRadius: 8, color: '#6b7280',
        }}>
          Dieser Bereich ist nur für Owner und Admin sichtbar.
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div style={{ padding: '2rem' }}>
        <p style={{ color: '#6b7280' }}>Lade Downloads-Catalog…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: '2rem' }}>
        <div style={{
          padding: '1rem', background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: 8, color: '#b91c1c',
        }}>
          {error}
        </div>
      </div>
    )
  }

  const products = data?.products ?? []
  // TQ #6007: null-safe filter — sub_products kann undefined/null sein.
  const regularProducts = products.filter((p) => !p.sub_products || p.sub_products.length === 0)
  const mcpProduct = products.find((p) => Array.isArray(p.sub_products) && p.sub_products.length > 0)

  return (
    <div style={{ padding: '1.5rem 2rem' }}>
      <h1 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem' }}>
        Downloads — IT-SCE Tools für deine Geräte
      </h1>
      <p style={{ color: '#6b7280', marginTop: 0, marginBottom: '1.5rem', fontSize: '0.9375rem' }}>
        Lade Hub, Agent und weitere Tools herunter. Aktivierung via Lizenz-Key im Tool selbst.
      </p>

      {products.length === 0 ? (
        <div style={{
          padding: '2rem', textAlign: 'center', background: '#f9fafb',
          border: '1px dashed #d1d5db', borderRadius: 8, color: '#6b7280',
        }}>
          Keine Downloads verfügbar. Bitte kontaktiere Support.
        </div>
      ) : (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem',
          }}>
            {regularProducts.map((p) => (
              <ProductCard key={p.slug} product={p} clientOS={clientOS} />
            ))}
          </div>

          {mcpProduct && (
            <div style={{
              border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', overflow: 'hidden',
            }}>
              <button
                onClick={() => setMcpExpanded((v) => !v)}
                style={{
                  width: '100%', padding: '1rem 1.25rem', background: 'transparent', border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  cursor: 'pointer', textAlign: 'left', fontSize: '0.9375rem', fontWeight: 600,
                }}
              >
                <span>
                  📦 {mcpProduct.name}{' '}
                  <span style={{
                    marginLeft: '0.5rem', padding: '2px 8px', borderRadius: 999,
                    background: '#e0e7ff', color: '#3730a3', fontSize: '0.75rem', fontWeight: 500,
                  }}>
                    {(mcpProduct.sub_products ?? []).length} Server
                  </span>
                </span>
                <span style={{ transform: mcpExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', color: '#9ca3af' }}>▶</span>
              </button>
              {mcpExpanded && (
                <div style={{ borderTop: '1px solid #f3f4f6', padding: '0.5rem' }}>
                  {(mcpProduct.sub_products ?? []).map((sub) => (
                    <ProductCard key={sub.slug} product={sub} clientOS={clientOS} compact />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function ProductCard({
  product,
  clientOS,
  compact = false,
}: {
  product: Product
  clientOS: string
  compact?: boolean
}) {
  const pad = compact ? '0.625rem 0.75rem' : '1rem 1.25rem'
  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
      padding: pad, marginBottom: compact ? '0.25rem' : 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        <div style={{
          width: 36, height: 36, borderRadius: 6, background: '#eff6ff',
          color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <DownloadIcon size={20} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
            <strong style={{ fontSize: compact ? '0.875rem' : '0.9375rem' }}>{product.name}</strong>
            {product.version_latest && (
              <span style={{
                padding: '1px 6px', borderRadius: 4, background: '#f3f4f6',
                color: '#6b7280', fontSize: '0.6875rem', fontFamily: 'monospace',
              }}>
                {product.version_latest}
              </span>
            )}
          </div>
          {!compact && product.description && (
            <p style={{
              margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.8125rem',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {product.description}
            </p>
          )}
        </div>
      </div>

      <div style={{
        marginTop: compact ? '0.5rem' : '0.875rem',
        display: 'flex', gap: '0.375rem', flexWrap: 'wrap',
      }}>
        {(product.platforms ?? []).length === 0 && (
          <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>Keine Builds verfügbar</span>
        )}
        {/* Gleiche Download-URL nur einmal zeigen (Hub 0.8.1 stand doppelt im Katalog) */}
        {(product.platforms ?? []).filter((pl, i, all) => all.findIndex((o) => o.url === pl.url) === i).map((pl) => {
          const osKey = normalizeOS(pl.os)
          const isRecommended = clientOS && osKey === clientOS
          return (
            <a
              key={`${pl.os}-${pl.arch}-${pl.url}`}
              href={pl.url}
              download={pl.file_name}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                padding: '0.4rem 0.7rem', borderRadius: 6,
                background: isRecommended ? '#2563eb' : '#f3f4f6',
                color: isRecommended ? '#fff' : '#374151',
                fontSize: '0.8125rem', textDecoration: 'none',
                border: isRecommended ? 'none' : '1px solid #e5e7eb',
                fontWeight: 500,
              }}
              title={`${pl.file_name ?? ''} ${formatBytes(pl.size_bytes)}`}
            >
              <span>{OS_ICON[osKey] ?? '💾'}</span>
              <span>{OS_LABEL[osKey] ?? pl.os}</span>
              {pl.arch && pl.arch !== 'amd64' && pl.arch !== 'x86_64' && (
                <span style={{ opacity: 0.7, fontSize: '0.6875rem' }}>{pl.arch}</span>
              )}
              {isRecommended && (
                <span style={{
                  padding: '0 5px', borderRadius: 4,
                  background: 'rgba(255,255,255,0.25)', fontSize: '0.625rem', fontWeight: 600,
                }}>
                  Empfohlen
                </span>
              )}
            </a>
          )
        })}
      </div>
    </div>
  )
}
